import { useEffect, useState } from "react";
import { BarChart3, Building2, ChevronRight, Fingerprint, Inbox, KeyRound, ListChecks, LogOut, MonitorSmartphone, DoorOpen, Settings, Smartphone, UserPlus, Users, UserSquare2, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../../connect/api";
import { useConnect } from "../../../connect/store";
import { Header, Spinner } from "../ui";

const Stat = ({ label, value, accent }) => (
  <div className="nxc-card p-3.5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`nxc-display text-2xl font-bold mt-1 ${accent || "text-white"}`}>{typeof value === "number" ? value.toLocaleString() : value}</div>
  </div>
);

const DeptBreakdown = ({ title, total, departments, accent = "bg-blue-500/70", testid }) => {
  const max = Math.max(1, ...departments.map((x) => x.n));
  return (
    <div className="nxc-card p-3.5" data-testid={testid}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 mb-2"><div className="text-sm font-semibold text-slate-200">{title}</div>{total != null && <div className="text-[11px] text-slate-500 shrink-0 whitespace-nowrap">{total.toLocaleString()} staff · {departments.length} departments</div>}</div>
      <ul className="space-y-1.5">
        {departments.map((d) => (
          <li key={d.name} className="text-xs">
            <div className="flex justify-between text-slate-400"><span className="truncate">{d.name}</span><span className="text-slate-200 font-medium ml-2">{d.n}</span></div>
            <div className="h-1 bg-white/5 rounded-full mt-1 overflow-hidden"><div className={`h-full ${accent} rounded-full`} style={{ width: `${(d.n / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AdminHome = () => {
  const { logout, badges } = useConnect();
  const [ov, setOv] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setOv(r.data)); }, []);

  const links = [
    { to: "/connect/admin/broadcast", icon: Megaphone, title: "CEO Broadcast", sub: "Message a person, a department or every group as Group CEO · replies inbox", gold: true },
    { to: "/connect/admin/inbox", icon: Inbox, title: "Staff-profile inbox", sub: "Reply as DM profiles", n: badges.inbox, gold: true },
    { to: "/connect/admin/send-as", icon: UserSquare2, title: "Send as DM profile", sub: "Message anyone or any group as any staff profile", gold: true },
    { to: "/connect/admin/queue", icon: ListChecks, title: "Corporate message queue", sub: "Review · approve · reschedule", n: badges.queue },
    { to: "/connect/admin/staff", icon: UserPlus, title: "Staff", sub: "Add, edit or remove logins" },
    { to: "/connect/admin/permissions", icon: KeyRound, title: "Chat permissions", sub: "Cross-department access" },
    { to: "/connect/admin/conversations", icon: Users, title: "All conversations", sub: "Every staff chat" },
    { to: "/connect/admin/devices", icon: Smartphone, title: "App installs & push", sub: ov ? `${ov.app_installed} installed · ${ov.app_confirmed} confirmed on device` : "Who has the Android app · send a test alert" },
    { to: "/connect/admin/sessions", icon: MonitorSmartphone, title: "Signed-in devices", sub: "Who is logged in where · sign out any phone or browser" },
    { to: "/connect/admin/door", icon: DoorOpen, title: "Door access", sub: "Holiday lock · grant timed access to staff" },
    { to: "/connect/admin/engagement", icon: BarChart3, title: "All Staff read report", sub: "Who opened today's posts · daily Telegram 6:30 PM" },
    { to: "/connect/admin/chat-access", icon: Fingerprint, title: "Chat access (attendance)", sub: "Punch-in opens · punch-out closes · WFH days · allow on blocked days" },
    { to: "/connect/admin/settings", icon: Settings, title: "Settings", sub: "Auto-reply wait · daily volume" },
  ];

  return (
    <div className="h-full flex flex-col">
      <Header title="CEO Admin" sub="All decisions here are final" right={<button onClick={logout} className="nxc-btn nxc-btn-ghost h-9 text-xs" data-testid="admin-logout-button"><LogOut size={14} /> Sign out</button>} />
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {!ov ? <div className="flex justify-center py-10"><Spinner /></div> : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Total staff" value={ov.total} />
              <Stat label="Awaiting your reply" value={ov.pending_replies} accent={ov.pending_replies ? "text-amber-300" : ""} />
              <Stat label="Pending in queue" value={ov.queue_pending} accent={ov.queue_pending ? "text-amber-300" : ""} />
              <Stat label="Posted today" value={ov.posted_today} />
              <Stat label="Conversations" value={ov.conversations} />
              <Stat label="App installed" value={ov.app_installed} accent={ov.app_installed ? "text-sky-300" : ""} />
              <Stat label="Push confirmed" value={ov.app_confirmed} accent={ov.app_confirmed ? "text-emerald-300" : ""} />
            </div>
            <ul className="space-y-1.5">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="nxc-card nxc-card-hover p-3 flex items-center gap-3" data-testid={`admin-link-${l.to.split("/").pop()}`}>
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center border ${l.gold ? "bg-amber-500/10 border-amber-500/30 text-amber-300" : "bg-blue-500/10 border-blue-500/25 text-blue-300"}`}><l.icon size={18} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-100">{l.title}</div>
                      <div className="text-xs text-slate-400">{l.sub}</div>
                    </div>
                    {l.n > 0 && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${l.gold ? "bg-amber-500 text-black" : "bg-blue-500 text-white"}`}>{l.n}</span>}
                    <ChevronRight size={16} className="text-slate-500" />
                  </Link>
                </li>
              ))}
            </ul>
            <div className="nxc-card p-3.5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-2"><Building2 size={15} className="text-blue-400" /> Offices</div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {ov.offices.map((o) => <li key={o.name} className="flex justify-between gap-2 text-slate-400"><span className="truncate">{o.name.replace("NST | ", "")}</span><span className="text-slate-200 font-medium shrink-0 whitespace-nowrap">{o.n.toLocaleString()}</span></li>)}
              </ul>
            </div>
            <DeptBreakdown title="NST | India — departments" total={ov.offices.find((o) => o.name === "NST | India")?.n} departments={ov.departments} testid="admin-depts-india" />
            {(ov.office_departments || []).map((o) => <DeptBreakdown key={o.office} title={`${o.office} — departments`} total={o.n} departments={o.departments} accent="bg-amber-400/70" testid={`admin-depts-${o.office.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />)}
          </>
        )}
      </div>
    </div>
  );
};
